import { useMemo } from 'react';

import { CommonEndpoints } from '@scaleaq/sitecontrol-lib';
import { useQueries } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import { CACHE_KEYS } from '@/constants';
import { useConfigStore, type SiteWithApis } from '@/stores';

import type { AuthUser } from '../authorizationHeader.ts';
import { fetchAuthApiGet } from '../fetchAuthApiGet.ts';

export type SiteInventoryHealth = 'healthy' | 'unhealthy';

const HEALTH_TIMEOUT_MS = 10_000;
/** Re-probe while the modal is open so a recovered site is not stuck as unhealthy. */
const HEALTH_REFETCH_INTERVAL_MS = 15_000;

type useGetSitesInventoryHealthProps = {
  sites: readonly SiteWithApis[];
  enabled: boolean;
  /** Probe the cloud inventory API even on an edge cluster. */
  forceCloud?: boolean;
};

/**
 * Probes the inventory API `GET /healthz` per site in parallel via {@link fetchAuthApiGet}
 * (the GET used by `useAuthApiGetSingleFetch`). That hook resolves only the current site's base
 * URL, so this keeps `useQueries` plus a per-site base URL: the site's edge `inventoryApi` on an
 * edge cluster, the cloud inventory API in portable (cloud) mode.
 *
 * Resolves to `'healthy'` only when the response is OK and the body is exactly `Healthy`
 * (capital H). A timeout (10s), network error, non-OK status, or any other body is `'unhealthy'`.
 * Returns `undefined` per site while that request is still in flight.
 */
export const useGetSitesInventoryHealth = ({
  sites,
  enabled,
  forceCloud = false,
}: useGetSitesInventoryHealthProps) => {
  const { user } = useAuth();
  const portableMode = useConfigStore(s => s.portableMode);

  return useQueries({
    queries: sites.map(site => {
      const inventoryApi = getHealthApi({ site, portableMode, forceCloud });
      return {
        queryKey: [CACHE_KEYS.inventoryHealth, site.id, inventoryApi],
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          fetchInventoryHealthy(inventoryApi, signal, user),
        enabled: enabled && Boolean(inventoryApi),
        retry: false,
        refetchOnWindowFocus: false,
        refetchInterval: HEALTH_REFETCH_INTERVAL_MS,
        staleTime: 0,
      };
    }),
    combine: results => {
      const bySiteId: Record<string, SiteInventoryHealth | undefined> = {};
      for (let i = 0; i < sites.length; i++) {
        const site = sites[i];
        const result = results[i];
        if (!site) continue;
        if (!getHealthApi({ site, portableMode, forceCloud })) {
          bySiteId[site.id] = 'unhealthy';
          continue;
        }
        // Cancelled/aborted probes have no data; do not treat that as unhealthy or it sticks in cache.
        if (!result || result.isPending || result.data == null) {
          bySiteId[site.id] = undefined;
          continue;
        }
        bySiteId[site.id] = result.data === 'healthy' ? 'healthy' : 'unhealthy';
      }
      return bySiteId;
    },
  });
};

/** Probes one site (`forceCloud` as in {@link useGetSitesInventoryHealth}). Returns `undefined` when `site` is missing or that probe is still in flight. */
export const useGetSiteInventoryHealth = (
  site: SiteWithApis | undefined,
  enabled = true,
  forceCloud = false
): SiteInventoryHealth | undefined => {
  const sites = useMemo(() => (site ? [site] : []), [site]);
  const healthBySiteId = useGetSitesInventoryHealth({
    sites,
    enabled: enabled && Boolean(site),
    forceCloud,
  });
  return site ? healthBySiteId[site.id] : undefined;
};

const getHealthApi = ({
  site,
  portableMode,
  forceCloud,
}: {
  site: SiteWithApis;
  portableMode: boolean | undefined;
  forceCloud: boolean;
}) => (portableMode || forceCloud ? site.apis.cloudInventoryApi : site.apis.inventoryApi);

export const fetchInventoryHealthy = async (
  inventoryApi: string,
  signal: AbortSignal,
  user?: AuthUser
): Promise<SiteInventoryHealth> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);
  const onAbort = () => controller.abort();
  signal.addEventListener('abort', onAbort);

  try {
    const body = await fetchAuthApiGet({
      api: inventoryApi,
      route: CommonEndpoints.health,
      user,
      signal: controller.signal,
      identifier: 'health',
      returnJsonResult: false,
      returnStringResult: true,
    });
    return typeof body === 'string' && body.trim() === 'Healthy' ? 'healthy' : 'unhealthy';
  } catch (error) {
    // Query cancel (modal close) must not be cached as unhealthy. Timeout and network errors should.
    if (signal.aborted) throw error;
    return 'unhealthy';
  } finally {
    window.clearTimeout(timeoutId);
    signal.removeEventListener('abort', onAbort);
  }
};
